import { isStepUpError, rejectIfStepUp, throwIfStepUp } from "../stepUp.ts";

export type StealthSessionView = {
  sessionId: string;
  deviceId: string;
  state: string;
  nonce: string | null;
  recordingRule: string | null;
  transcriptHealthy: boolean;
  signalr: { url: string; accessToken: string; method: string };
};

export type StealthAlertView = {
  alertId: string;
  sessionId: string | null;
  deviceId: string;
  operatorUpn: string | null;
  occurredAt: string;
  kind: string | null;
};

export function stealthAlertSentence(alert: StealthAlertView): string {
  if (alert.kind === "command-gained") {
    return `Stealth Command capability gained on ${alert.deviceId}.`;
  }
  return `Stealth Command session started by ${alert.operatorUpn} on ${alert.deviceId}.`;
}

export function parseStealthAlerts(body: unknown): StealthAlertView[] | null {
  if (!Array.isArray(body)) return null;
  const alerts: StealthAlertView[] = [];
  for (const item of body) {
    if (!item || typeof item !== "object") continue;
    const record = item as Record<string, unknown>;
    const alertId = text(record.alertId);
    const deviceId = text(record.deviceId);
    const occurredAt = text(record.occurredAt);
    const kind = text(record.kind);
    if (kind === "command-gained") {
      if (!alertId || !deviceId || !occurredAt) continue;
      alerts.push({ alertId, sessionId: null, deviceId, operatorUpn: null, occurredAt, kind });
      continue;
    }
    const sessionId = text(record.sessionId);
    const operatorUpn = text(record.operatorUpn);
    if (!alertId || !sessionId || !deviceId || !operatorUpn || !occurredAt) continue;
    alerts.push({ alertId, sessionId, deviceId, operatorUpn, occurredAt, kind });
  }
  return alerts;
}

async function send(
  apiBase: string,
  token: () => Promise<string>,
  path: string,
  init?: RequestInit,
  redirectOnStepUp = true,
): Promise<Response> {
  const accessToken = await token();
  const headers = new Headers(init?.headers);
  headers.set("authorization", `Bearer ${accessToken}`);
  headers.set("accept", "application/json");
  if (init?.body) headers.set("content-type", "application/json");
  const response = await fetch(new URL(path, apiBase), { ...init, headers });
  if (redirectOnStepUp) await rejectIfStepUp(response);
  else await throwIfStepUp(response);
  return response;
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

export function parseStealthSession(value: unknown): StealthSessionView | null {
  if (!value || typeof value !== "object") return null;
  const record = value as Record<string, unknown>;
  const sessionId = text(record.sessionId);
  const deviceId = text(record.deviceId);
  const state = text(record.state);
  const recordingRule = text(record.recordingRule) ?? "";
  const signalr = record.signalr;
  if (!sessionId || !deviceId || !state || !signalr || typeof signalr !== "object") return null;
  const hub = signalr as Record<string, unknown>;
  const url = text(hub.url);
  const accessToken = text(hub.accessToken);
  const method = text(hub.method);
  if (!url || !accessToken || !method) return null;
  return {
    sessionId,
    deviceId,
    state,
    nonce: typeof record.nonce === "string" ? record.nonce : null,
    recordingRule,
    transcriptHealthy: record.transcriptHealthy === true,
    signalr: { url, accessToken, method },
  };
}

export async function openStealthSession(
  apiBase: string,
  token: () => Promise<string>,
  deviceId: string,
  justification: string,
  reason = "",
  redirectOnStepUp = true,
): Promise<StealthSessionView> {
  const response = await send(
    apiBase,
    token,
    "/v1/stealth-command/sessions",
    {
      method: "POST",
      body: JSON.stringify({ deviceId, justification, reason }),
    },
    redirectOnStepUp,
  );
  if (!response.ok) {
    let detail = "";
    try {
      const problem = (await response.json()) as { detail?: unknown };
      if (typeof problem.detail === "string") detail = problem.detail;
    } catch {
      detail = "";
    }
    throw new Error(detail.length > 0 ? detail : "Stealth Command could not open.");
  }
  const parsed = parseStealthSession(await response.json());
  if (!parsed) throw new Error("Stealth Command session was unreadable.");
  return parsed;
}

export async function readStealthSession(
  apiBase: string,
  token: () => Promise<string>,
  sessionId: string,
): Promise<StealthSessionView> {
  const response = await send(apiBase, token, `/v1/stealth-command/sessions/${sessionId}`);
  if (!response.ok) throw new Error(await response.text());
  const parsed = parseStealthSession(await response.json());
  if (!parsed) throw new Error("Stealth Command session was unreadable.");
  return parsed;
}

export async function endStealthSession(apiBase: string, token: () => Promise<string>, sessionId: string): Promise<void> {
  const response = await send(apiBase, token, `/v1/stealth-command/sessions/${sessionId}/end`, { method: "POST" });
  if (!response.ok) throw new Error(await response.text());
}

export async function loadStealthAlerts(apiBase: string, token: () => Promise<string>): Promise<StealthAlertView[] | null> {
  let response: Response;
  try {
    response = await send(apiBase, token, "/v1/stealth-command/alerts");
  } catch {
    return null;
  }
  if (response.status === 404 || response.status === 403) return null;
  if (!response.ok) return null;
  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return null;
  }
  return parseStealthAlerts(body);
}

export async function readStealthPolicy(
  apiBase: string,
  token: () => Promise<string>,
  organisationId: string,
): Promise<boolean | null> {
  try {
    const response = await send(apiBase, token, `/v1/organisations/${organisationId}/stealth-command`);
    if (!response.ok) return null;
    const body: unknown = await response.json();
    if (!body || typeof body !== "object") return null;
    const enabled = (body as Record<string, unknown>).enabled;
    return typeof enabled === "boolean" ? enabled : null;
  } catch {
    return null;
  }
}

export type GroupStealthView = {
  enabled: boolean;
  departments: { departmentId: string; enabled: boolean }[];
  tags: { key: string; value: string; enabled: boolean }[];
};

export async function readGroupStealth(
  apiBase: string,
  token: () => Promise<string>,
  organisationId: string,
): Promise<GroupStealthView | null> {
  try {
    const response = await send(apiBase, token, `/v1/organisations/${organisationId}/stealth-command/groups`);
    if (!response.ok) return null;
    const body: unknown = await response.json();
    if (!body || typeof body !== "object") return null;
    const record = body as Record<string, unknown>;
    if (typeof record.enabled !== "boolean" || !Array.isArray(record.departments) || !Array.isArray(record.tags)) return null;
    const departments: GroupStealthView["departments"] = [];
    for (const item of record.departments) {
      if (!item || typeof item !== "object") continue;
      const row = item as Record<string, unknown>;
      if (typeof row.departmentId === "string" && typeof row.enabled === "boolean") {
        departments.push({ departmentId: row.departmentId, enabled: row.enabled });
      }
    }
    const tags: GroupStealthView["tags"] = [];
    for (const item of record.tags) {
      if (!item || typeof item !== "object") continue;
      const row = item as Record<string, unknown>;
      if (typeof row.key === "string" && row.key.length > 0 && typeof row.value === "string" && typeof row.enabled === "boolean") {
        tags.push({ key: row.key, value: row.value, enabled: row.enabled });
      }
    }
    return { enabled: record.enabled, departments, tags };
  } catch {
    return null;
  }
}

export async function writeDepartmentStealth(
  apiBase: string,
  token: () => Promise<string>,
  organisationId: string,
  departmentId: string,
  enabled: boolean,
): Promise<string | null> {
  return writeEnabled(apiBase, token, `/v1/organisations/${organisationId}/departments/${departmentId}/stealth-command`, { enabled });
}

export async function writeTagStealth(
  apiBase: string,
  token: () => Promise<string>,
  organisationId: string,
  key: string,
  value: string,
  enabled: boolean,
): Promise<string | null> {
  return writeEnabled(apiBase, token, `/v1/organisations/${organisationId}/tags/stealth-command`, { key, value, enabled });
}

async function writeEnabled(
  apiBase: string,
  token: () => Promise<string>,
  path: string,
  body: Record<string, unknown>,
): Promise<string | null> {
  try {
    const response = await send(apiBase, token, path, { method: "PUT", body: JSON.stringify(body) });
    if (response.ok) return null;
    return await response.text();
  } catch (cause) {
    if (isStepUpError(cause)) throw cause;
    return "Stealth Command policy was not saved.";
  }
}

export async function writeStealthPolicy(
  apiBase: string,
  token: () => Promise<string>,
  organisationId: string,
  enabled: boolean,
): Promise<string | null> {
  try {
    const response = await send(apiBase, token, `/v1/organisations/${organisationId}/stealth-command`, {
      method: "PUT",
      body: JSON.stringify({ enabled }),
    });
    if (response.ok) return null;
    return await response.text();
  } catch (cause) {
    if (isStepUpError(cause)) throw cause;
    return "Stealth Command policy was not saved.";
  }
}
